import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiX, FiCopy, FiEdit2 } from 'react-icons/fi';
import Loader from '../../../components/Loader';
import Verificacion from '../../../components/Verificacion';
import BASE_URL from '../../../config/config';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PermisoDenegado from '../../../components/PermisoDenegado';
import './css/admin_style.css';

const AjustesDelSistema = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('AuthToken');
  const tokenInfo = token ? jwtDecode(token) : {};

  const [minimoTiempoCumplido, setMinimoTiempoCumplido] = useState(false);
  const [cargandoLista, setCargandoLista] = useState(true);
  const [ajustes, setAjustes] = useState([]);

  // Filtros y paginación
  const [busqueda, setBusqueda] = useState('');
  const [orden, setOrden] = useState('asc'); // 'asc' | 'desc'
  const [paginaActual, setPaginaActual] = useState(1);
  const [porPagina, setPorPagina] = useState(12);

  // Crear (panel plegable)
  const [mostrarCrear, setMostrarCrear] = useState(false);
  const [nuevoNombre, setNuevoNombre] = useState('');
  const [nuevoValor, setNuevoValor] = useState('');
  const [nuevoTipoValor, setNuevoTipoValor] = useState('texto'); // 'texto' | 'json'
  const [creando, setCreando] = useState(false);

  // Editar
  const [editandoId, setEditandoId] = useState(null);
  const [editNombre, setEditNombre] = useState('');
  const [editValor, setEditValor] = useState('');
  const [editTipoValor, setEditTipoValor] = useState('texto');
  const [guardando, setGuardando] = useState(false);

  // Loader mínimo 1s
  useEffect(() => {
    const t = setTimeout(() => setMinimoTiempoCumplido(true), 1000);
    return () => clearTimeout(t);
  }, []);

  // Cargar lista
  const cargarAjustes = async () => {
    setCargandoLista(true);
    try {
      const resp = await axios.get(`${BASE_URL}/ajustes`, {
        headers: { Authorization: token },
      });
      if (resp.data?.ajustes) {
        setAjustes(resp.data.ajustes);
      } else {
        setAjustes([]);
      }
    } catch (e) {
      toast.error('Error al cargar los ajustes.');
      setAjustes([]);
    } finally {
      setCargandoLista(false);
    }
  };

  useEffect(() => {
    if (tokenInfo?.role === 'admin') {
      cargarAjustes();
    }
  }, [tokenInfo?.role]);

  // Helpers
  const esJSONValido = (txt) => {
    try {
      const parsed = JSON.parse(txt);
      return typeof parsed === 'object';
    } catch {
      return false;
    }
  };

  const formatearJSON = (txt) => {
    try {
      return JSON.stringify(JSON.parse(txt), null, 2);
    } catch {
      return txt;
    }
  };

  const detectarTipoValor = (valor) => (esJSONValido(valor) ? 'json' : 'texto');

  // Derivados
  const ajustesFiltradosOrdenados = useMemo(() => {
    const term = busqueda.trim().toLowerCase();
    let list = ajustes.filter((a) => {
      const n = (a.nombre || '').toLowerCase();
      const v = (a.valor || '').toLowerCase();
      return !term || n.includes(term) || v.includes(term);
    });

    list = list.sort((a, b) => {
      if (orden === 'asc') return a.nombre.localeCompare(b.nombre);
      return b.nombre.localeCompare(a.nombre);
    });

    return list;
  }, [ajustes, busqueda, orden]);

  const totalPaginas = Math.ceil(ajustesFiltradosOrdenados.length / porPagina) || 1;
  const indicePrimero = (paginaActual - 1) * porPagina;
  const pagina = ajustesFiltradosOrdenados.slice(indicePrimero, indicePrimero + porPagina);

  const resetCrear = () => {
    setNuevoNombre('');
    setNuevoValor('');
    setNuevoTipoValor('texto');
  };

  const empezarEditar = (item) => {
    setEditandoId(item.id ?? item.nombre);
    setEditNombre(item.nombre);
    const valorComoTexto = typeof item.valor === 'string' ? item.valor : String(item.valor ?? '');
    setEditValor(valorComoTexto);
    setEditTipoValor(detectarTipoValor(valorComoTexto));
  };

  const cancelarEditar = () => {
    setEditandoId(null);
    setEditNombre('');
    setEditValor('');
    setEditTipoValor('texto');
  };

  const copiar = async (texto, msgOk = 'Copiado al portapapeles.') => {
    try {
      await navigator.clipboard.writeText(texto);
      toast.success(msgOk);
    } catch {
      toast.error('No se pudo copiar al portapapeles.');
    }
  };

  // Acciones
  const crearAjuste = async () => {
    if (!nuevoNombre.trim()) {
      return toast.error('El nombre es obligatorio.');
    }
    if (nuevoTipoValor === 'json' && !esJSONValido(nuevoValor)) {
      return toast.error('El valor no es un JSON válido.');
    }
    setCreando(true);
    try {
      const payload = {
        nombre: nuevoNombre.trim(),
        valor: nuevoTipoValor === 'json' ? JSON.parse(nuevoValor) : nuevoValor,
      };
      await axios.post(`${BASE_URL}/ajustes`, payload, {
        headers: { Authorization: token },
      });
      toast.success('Ajuste creado.');
      resetCrear();
      setMostrarCrear(false);
      await cargarAjustes();
      setPaginaActual(1);
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.error('Ya existe un ajuste con ese nombre.');
      } else {
        toast.error('Error al crear el ajuste.');
      }
    } finally {
      setCreando(false);
    }
  };

  const guardarEdicion = async (item) => {
    if (!editNombre.trim()) {
      return toast.error('El nombre es obligatorio.');
    }
    if (editTipoValor === 'json' && !esJSONValido(editValor)) {
      return toast.error('El valor no es un JSON válido.');
    }
    setGuardando(true);
    try {
      const body = {
        valor: editTipoValor === 'json' ? JSON.parse(editValor) : editValor,
      };
      if (editNombre !== item.nombre) {
        body.nombre = editNombre.trim();
      }
      await axios.put(`${BASE_URL}/ajustes/${encodeURIComponent(item.nombre)}`, body, {
        headers: { Authorization: token },
      });
      toast.success('Ajuste actualizado.');
      cancelarEditar();
      await cargarAjustes();
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.error('Ya existe otro ajuste con el nombre nuevo.');
      } else if (e?.response?.status === 404) {
        toast.error('El ajuste ya no existe.');
      } else {
        toast.error('Error al actualizar el ajuste.');
      }
    } finally {
      setGuardando(false);
    }
  };

  // Loader fullscreen como en el resto
  const mostrarLoader = !minimoTiempoCumplido || (tokenInfo.role === 'admin' && cargandoLista);

  if (tokenInfo.estado === 'verificar') {
    return <Verificacion />;
  }

  if (tokenInfo.role !== 'admin') {
    return <PermisoDenegado />;
  }

  if (mostrarLoader) {
    return (
      <div className="loader-fullscreen">
        <Loader />
      </div>
    );
  }

  return (
    <main className="site-width">
      <ToastContainer position="bottom-right" autoClose={3000} />
      <button className="boton-volver" onClick={() => navigate('/dashboard')}>
        ← Volver
      </button>
      <div className="dashboard-content gap-ajustes">
        <div className="pagina-cabecera">
          <h2>Ajustes del sistema</h2>
          <p>Administra pares <b>nombre → valor</b>. El valor puede ser texto o JSON.</p>
        </div>

        {/* Barra de herramientas */}
        <div className="ajustes-toolbar">
          <input
            type="text"
            placeholder="Buscar por nombre o valor..."
            value={busqueda}
            onChange={(e) => {
              setBusqueda(e.target.value);
              setPaginaActual(1);
            }}
          />
          <div className="ajustes-toolbar-acciones">
            <select value={orden} onChange={(e) => setOrden(e.target.value)}>
              <option value="asc">Nombre (A→Z)</option>
              <option value="desc">Nombre (Z→A)</option>
            </select>
            <select
              className="selector-page-size"
              value={porPagina}
              onChange={(e) => { setPorPagina(Number(e.target.value)); setPaginaActual(1); }}
            >
              <option value={12}>12 / página</option>
              <option value={24}>24 / página</option>
              <option value={50}>50 / página</option>
            </select>
            <button
              type="button"
              className={`chip-toggle ${mostrarCrear ? 'activo' : ''}`}
              onClick={() => setMostrarCrear((v) => !v)}
            >
              <FiPlus /> Nuevo ajuste
            </button>
          </div>
        </div>

        {/* Panel plegable: crear ajuste */}
        {mostrarCrear && (
          <div className="ajustes-panel-crear">
            <div className="ajustes-panel-crear-header">
              <h3>Nuevo ajuste</h3>
              <button type="button" className="close-inline" onClick={() => setMostrarCrear(false)} aria-label="Cerrar">
                <FiX />
              </button>
            </div>

            <div className="ajustes-crear-grid">
              <div className="campo-ajuste">
                <label htmlFor="nuevo-nombre">Nombre</label>
                <input
                  id="nuevo-nombre"
                  type="text"
                  placeholder="p.ej. horario_mañana"
                  value={nuevoNombre}
                  onChange={(e) => setNuevoNombre(e.target.value)}
                />
              </div>
              <div className="campo-ajuste">
                <label>Tipo de valor</label>
                <div className="selector-tipo-valor">
                  <label>
                    <input
                      type="radio"
                      name="tipo-crear"
                      checked={nuevoTipoValor === 'texto'}
                      onChange={() => setNuevoTipoValor('texto')}
                    />
                    <span>Texto</span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="tipo-crear"
                      checked={nuevoTipoValor === 'json'}
                      onChange={() => {
                        setNuevoTipoValor('json');
                        if (nuevoValor.trim() && esJSONValido(nuevoValor)) {
                          setNuevoValor(formatearJSON(nuevoValor));
                        }
                      }}
                    />
                    <span>JSON</span>
                  </label>
                </div>
              </div>
              <div className="campo-ajuste full">
                <label htmlFor="nuevo-valor">Valor</label>
                {nuevoTipoValor === 'json' ? (
                  <textarea
                    id="nuevo-valor"
                    rows={6}
                    placeholder='{"inicio":"08:00","fin":"14:00"}'
                    value={nuevoValor}
                    onChange={(e) => setNuevoValor(e.target.value)}
                  />
                ) : (
                  <input
                    id="nuevo-valor"
                    type="text"
                    placeholder="Texto libre"
                    value={nuevoValor}
                    onChange={(e) => setNuevoValor(e.target.value)}
                  />
                )}
                {nuevoTipoValor === 'json' && (
                  <div className="acciones-json">
                    <button
                      type="button"
                      onClick={() => setNuevoValor(formatearJSON(nuevoValor))}
                      disabled={!nuevoValor.trim() || !esJSONValido(nuevoValor)}
                    >
                      Formatear JSON
                    </button>
                    <span className={`ajustes-json-badge ${!nuevoValor.trim() ? '' : esJSONValido(nuevoValor) ? 'ok' : 'error'}`}>
                      {!nuevoValor.trim() ? '—' : esJSONValido(nuevoValor) ? 'JSON válido' : 'JSON inválido'}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="ajustes-crear-actions">
              <button type="button" className="ajustes-btn-secundario" onClick={resetCrear} disabled={creando}>Limpiar</button>
              <button type="button" className="ajustes-btn-primario" onClick={crearAjuste} disabled={creando}>
                {creando ? 'Creando…' : 'Crear ajuste'}
              </button>
            </div>
          </div>
        )}

        {/* Tabla de ajustes */}
        <div className="ajustes-panel">
          <div className="ajustes-list-header">
            <span>Nombre</span>
            <span>Valor</span>
            <span>Acciones</span>
          </div>

          <div className="ajustes-rows">
            {pagina.length === 0 ? (
              <div className="ajustes-vacio">No hay ajustes que coincidan con la búsqueda.</div>
            ) : (
              pagina.map((item) => {
                const key = item.id ?? item.nombre;
                const valorTexto = typeof item.valor === 'string' ? item.valor : String(item.valor ?? '');
                const valorEsJSON = esJSONValido(valorTexto);
                const valorPrettified = valorEsJSON ? formatearJSON(valorTexto) : valorTexto;
                const enEdicion = editandoId === (item.id ?? item.nombre);

                return (
                  <div className={`ajustes-row ${enEdicion ? 'ajustes-row--editando' : ''}`} key={key}>
                    {enEdicion ? (
                      <div className="ajustes-form-edicion">
                        <div className="campo-ajuste">
                          <label>Nombre</label>
                          <input
                            type="text"
                            value={editNombre}
                            onChange={(e) => setEditNombre(e.target.value)}
                          />
                        </div>

                        <div className="campo-ajuste">
                          <label>Tipo de valor</label>
                          <div className="selector-tipo-valor">
                            <label>
                              <input
                                type="radio"
                                name={`tipo-${key}`}
                                checked={editTipoValor === 'texto'}
                                onChange={() => setEditTipoValor('texto')}
                              />
                              <span>Texto</span>
                            </label>
                            <label>
                              <input
                                type="radio"
                                name={`tipo-${key}`}
                                checked={editTipoValor === 'json'}
                                onChange={() => {
                                  setEditTipoValor('json');
                                  if (esJSONValido(editValor)) {
                                    setEditValor(formatearJSON(editValor));
                                  }
                                }}
                              />
                              <span>JSON</span>
                            </label>
                          </div>
                        </div>

                        <div className="campo-ajuste full">
                          <label>Valor</label>
                          {editTipoValor === 'json' ? (
                            <textarea
                              rows={7}
                              value={editValor}
                              onChange={(e) => setEditValor(e.target.value)}
                            />
                          ) : (
                            <input
                              type="text"
                              value={editValor}
                              onChange={(e) => setEditValor(e.target.value)}
                            />
                          )}
                          {editTipoValor === 'json' && (
                            <div className="acciones-json">
                              <button
                                type="button"
                                onClick={() => setEditValor(formatearJSON(editValor))}
                                disabled={!editValor.trim() || !esJSONValido(editValor)}
                              >
                                Formatear JSON
                              </button>
                              <span className={`ajustes-json-badge ${!editValor.trim() ? '' : esJSONValido(editValor) ? 'ok' : 'error'}`}>
                                {!editValor.trim() ? '—' : esJSONValido(editValor) ? 'JSON válido' : 'JSON inválido'}
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="ajustes-crear-actions">
                          <button type="button" className="ajustes-btn-secundario" onClick={cancelarEditar} disabled={guardando}>
                            Cancelar
                          </button>
                          <button type="button" className="ajustes-btn-primario" onClick={() => guardarEdicion(item)} disabled={guardando}>
                            {guardando ? 'Guardando…' : 'Guardar'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="ajustes-col col-nombre">
                          <code className="ajustes-badge-key">{item.nombre}</code>
                        </div>

                        <div className="ajustes-col col-valor">
                          <span className={`ajustes-tipo-badge ${valorEsJSON ? 'json' : 'texto'}`}>
                            {valorEsJSON ? 'JSON' : 'Texto'}
                          </span>
                          {valorEsJSON ? (
                            <pre className="ajustes-valor-json">{valorPrettified}</pre>
                          ) : (
                            <p className="ajustes-valor-texto">{valorPrettified}</p>
                          )}
                        </div>

                        <div className="ajustes-col col-acciones">
                          <button type="button" className="ajustes-btn-icono" onClick={() => empezarEditar(item)} title="Editar">
                            <FiEdit2 />
                          </button>
                          <button type="button" className="ajustes-btn-icono" onClick={() => copiar(valorTexto)} title="Copiar valor">
                            <FiCopy />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Paginación */}
        {ajustesFiltradosOrdenados.length > porPagina && (
          <div className="pagination">
            <button
              onClick={() => setPaginaActual((p) => Math.max(1, p - 1))}
              disabled={paginaActual === 1}
            >
              Anterior
            </button>
            <span>Página {paginaActual} de {totalPaginas}</span>
            <button
              onClick={() => setPaginaActual((p) => Math.min(totalPaginas, p + 1))}
              disabled={paginaActual === totalPaginas}
            >
              Siguiente
            </button>
          </div>
        )}
      </div>
    </main>
  );
};

export default AjustesDelSistema;
